<script setup lang="ts">
import { History, RotateCcw } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, h, ref } from "vue";
import { RouterLink, useRouter } from "vue-router";
import { toast } from "vue-sonner";
import type { Operation } from "@/api/contracts";
import { confirmationText, undoOperation, useOperations } from "@/api/operations";
import DataTable from "@/components/common/DataTable.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import RiskBadge from "@/components/common/RiskBadge.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import { Button } from "@/components/ui/button";
import { confirm } from "@/composables/useConfirm";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import type { Column } from "@/lib/table";

definePage({ meta: { title: "Changes" } });

const router = useRouter();
const queryClient = useQueryClient();
const { me } = useSession();
const status = ref<string | undefined>();
const filter = ref("");
const ops = useOperations(() => ({ status: status.value }));

const STATUSES = ["awaiting_approval", "executing", "executed", "declined", "expired", "failed", "undoing", "undone"].map((s) => ({ value: s, label: t.approvals.status[s] ?? s }));

async function undo(op: Operation) {
  // Undo needs what the change needed: high risk ⇒ type the target.
  const typed = op.risk === "high" ? confirmationText(op) : undefined;
  const answer = await confirm({ title: `${t.changes.undo}: ${op.plan.summary}`, description: t.changes.undoConfirm, confirmLabel: t.changes.undo, ...(typed ? { typeToConfirm: typed } : {}) });
  if (!answer) return;
  try {
    const res = await undoOperation(op.id, typed);
    if (res.status === "awaiting_second_approver" && res.operationId) {
      toast.success(t.changes.undoAwaitingSecond);
      await router.push(`/approvals/${res.operationId}`);
    } else toast.success(t.changes.undoStarted);
    await queryClient.invalidateQueries({ queryKey: ["operations"] });
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

const columns = computed<Column<Operation>[]>(() => [
  {
    id: "summary",
    header: "Change",
    accessorFn: (o) => `${o.plan.summary} ${o.tool} ${o.target ?? ""}`,
    meta: { mobile: "title" },
    cell: ({ row }) =>
      h("div", { class: "whitespace-normal sm:min-w-56" }, [
        h(RouterLink, { to: `/approvals/${row.original.id}`, class: "line-clamp-2 font-medium [overflow-wrap:anywhere] hover:underline", onClick: (e: Event) => e.stopPropagation() }, () => row.original.plan.summary || row.original.tool),
        h("p", { class: "mt-0.5 font-mono text-xs font-normal text-muted-foreground [overflow-wrap:anywhere]" }, [row.original.tool, row.original.target ? ` · ${row.original.target}` : ""]),
      ]),
  },
  { id: "risk", header: "Risk", accessorFn: (o) => o.risk, cell: ({ row }) => h(RiskBadge, { risk: row.original.risk }) },
  { id: "status", header: "Status", accessorFn: (o) => o.status, meta: { mobile: "badge" }, cell: ({ row }) => h(StatusBadge, { status: row.original.status }) },
  { id: "when", header: "When", accessorFn: (o) => o.createdAt, meta: { mobile: "subtitle" }, cell: ({ row }) => h(RelativeTime, { value: row.original.createdAt, class: "text-sm whitespace-nowrap" }) },
  {
    id: "actions",
    header: "",
    enableSorting: false,
    meta: { mobile: "actions", align: "end" },
    cell: ({ row }) =>
      row.original.status === "executed" && row.original.reversible && row.original.principalId === me.value?.id
        ? h(Button, { size: "sm", variant: "outline", onClick: (e: Event) => (e.stopPropagation(), undo(row.original)) }, () => [h(RotateCcw), t.changes.undo])
        : null,
  },
]);
</script>

<template>
  <div>
    <PageHeader :title="t.changes.title" :description="t.changes.description" :crumbs="[{ label: t.nav.approvals, to: '/approvals' }, { label: t.changes.title }]" />
    <FilterBar v-model:search="filter" placeholder="Filter changes…" label="Filter changes" class="mb-5">
      <SimpleSelect v-model="status" :options="STATUSES" allow-empty :empty-label="t.common.all + ' statuses'" placeholder="All statuses" class="w-full sm:w-48" aria-label="Status" />
    </FilterBar>
    <ListSkeleton v-if="ops.isPending.value" :rows="5" />
    <ErrorState v-else-if="ops.error.value" :error="ops.error.value" @retry="ops.refetch()" />
    <EmptyState v-else-if="!ops.data.value?.length" :icon="History" :title="t.changes.empty" description="Changes you make with Claude (e.g. mysql_change, grafana_change) appear here with their plan and undo." />
    <DataTable v-else :data="ops.data.value" :columns="columns" :filter="filter" row-clickable caption="Changes" @row-click="(o) => router.push(`/approvals/${o.id}`)" />
  </div>
</template>
